import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '../../themes/dark';
 import { lightTheme } from '../../themes/light';
import { GlobalStyles } from '../../globals';
import { FiArrowLeft } from 'react-icons/fi';
import Header from '../../components/header/header';
import Tooltip from '../../components/tooltip';
import {
  Container,
  BackButton,
  Title,
  AccountContainer,
  FormContainer,
  FormGroup,
  Label,
  Input,
  SubmitButton,
  Divider,
  ErrorMessage,
  SuccessMessage,
  ContentWrapper
} from './styles';
import Loading from '../../components/Loading';

const Account: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userName, setUserName] = useState('');
  
  // Sempre usar tema dark
  // Tema claro, como o resto da aplicacao (ver a nota em layout.tsx).
  const theme = lightTheme;

  // Helper para renderizar ícones corretamente
  const renderIcon = (Icon: any, props: any) => {
    return <Icon {...props} />;
  };

  // Busca informações do usuário
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (user) {
        try {
          const token = localStorage.getItem('@App:token');
          const storedUser = localStorage.getItem('@App:user');
          const userId = storedUser ? JSON.parse(storedUser).id : user.id;
          
          const response = await api.get(`/api/users/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data && response.data.name) {
            setUserName(response.data.name);
          }
        } catch (error) {
          console.error('Erro ao buscar informações do usuário:', error);
        }
      }
    };

    fetchUserInfo();
  }, [user]);

  // Simulate initial page loading
  useEffect(() => {
    // Simular o carregamento inicial da página
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  // Função de tema removida - apenas dark theme

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validações
    if (!currentPassword || !password || !confirmPassword) {
      setError('Preencha todos os campos');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('A nova senha e a senha de confirmação não coincidem.');
      return;
    }
    
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    
    setError('');
    setSuccess('');
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('@App:token');
      
      // Atualiza a senha do usuário com o formato correto para o backend
      const requestData = {
        currentPassword: currentPassword,
        newPassword: password,
        confirmPassword: confirmPassword
      };
      
      // Usando o endpoint PATCH correto com token no header
      await api.patch('/api/change-password', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Limpa os campos
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');
      setSuccess('Senha alterada com sucesso!');
    } catch (error: any) {
      if (error.response) {
        // Tratamento específico baseado no código de erro da API
        if (error.response.status === 401 || error.response.data?.message?.includes('current password')) {
          setError('A senha atual está incorreta. Por favor, verifique e tente novamente.');
        } else if (error.response.data?.message?.includes('match')) {
          setError('A nova senha e a senha de confirmação não coincidem.');
        } else {
          setError(error.response.data?.message || 'Erro ao processar sua solicitação. Por favor, tente novamente.');
        }
      } else {
        setError('Erro ao processar sua solicitação. Por favor, tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme as any}>
      <GlobalStyles />
      {isPageLoading && <Loading fullScreen />}
      {isLoading && <Loading />}
      
      <div className="root-layout">
        <Header />
        <Container>
          <ContentWrapper>
            <Tooltip text="Voltar para o Dashboard">
              <BackButton onClick={handleBack}>
                {renderIcon(FiArrowLeft, { className: "icon", size: 30 })}
              </BackButton>
            </Tooltip>
            
            {!isPageLoading && (
              <AccountContainer>
                <Title>Minha Conta</Title>
                
                <FormContainer>
                  <FormGroup>
                    <Label>Nome</Label>
                    <Input 
                      type="text" 
                      value={userName || user?.name || ''} 
                      disabled 
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label>Email</Label>
                    <Input 
                      type="email" 
                      value={user?.email || ''} 
                      disabled 
                    />
                  </FormGroup>
                  
                  <Divider />
                  
                  <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                    <FormGroup>
                      <Label>Senha atual</Label>
                      <Input 
                        type="password" 
                        value={currentPassword} 
                        onChange={(e) => setCurrentPassword(e.target.value)} 
                        placeholder="********"
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>Nova senha</Label>
                      <Input 
                        type="password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        placeholder="********"
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>Confirmar nova senha</Label>
                      <Input 
                        type="password" 
                        value={confirmPassword} 
                        onChange={(e) => setConfirmPassword(e.target.value)} 
                        placeholder="********"
                      />
                    </FormGroup>
                    
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                      {error && <ErrorMessage>{error}</ErrorMessage>}
                      {success && <SuccessMessage>{success}</SuccessMessage>}
                      
                      <SubmitButton type="submit" disabled={isLoading}>
                        Alterar senha
                      </SubmitButton>
                    </div>
                  </form>
                </FormContainer>
              </AccountContainer>
            )}
          </ContentWrapper>
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default Account; 